import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { push as changeLocation } from 'connected-react-router';
import isEqual from 'react-fast-compare';
import _ from 'lodash';
import { Button, Intent } from '@blueprintjs/core';

import { BottomRightToaster } from '@f/services/toasters';
import { SearchParameterForm, SearchParameterFormProps } from '@f/components/shared/search-parameter-form';
import {
  patchReactionSearchPreferences,
  createReactionSearchPreferences,
} from '@f/services/reaction-search-preference/reaction-search-preference.actions';
import { useSearchParamsValidation } from '@f/components/shared/search-parameter-form/services/useSearchParamsValidation';

import { PresetEditable, Preset } from '../Preset';

import cls from './PresetForm.module.css';
import { PresetMetaForm } from './PresetMetaForm';

interface PresetFormProps {
  presetId?: string;
  presetData: PresetEditable | Preset;
  presetNames: string[];
  isPatching: boolean;
  createReactionSearchPreferences: (preset: PresetEditable) => Promise<void>;
  patchReactionSearchPreferences: (preset: Preset) => Promise<void>;
  changeLocation: typeof changeLocation;
}

function PresetForm(props: PresetFormProps) {
  const {
    presetId,
    presetData,
    presetNames,
    isPatching,
    createReactionSearchPreferences,
    patchReactionSearchPreferences,
    changeLocation,
  } = props;

  const isNew = !presetId;

  const [preset, setPreset] = useState(presetData);
  const { validate } = useSearchParamsValidation(preset.searchParams);

  const isNameDuplicate = (name: string) => name !== presetData.name && presetNames.includes(name);
  const canBeSaved = (preset: PresetEditable) => {
    if (isPatching) {
      return false;
    }
    const validation = validate(preset.searchParams);
    if (!validation.isValid) {
      return false;
    }
    if (preset.name === '' || isNameDuplicate(preset.name)) {
      return false;
    }
    return true;
  };

  const isSubmitDisabled = !canBeSaved(preset);

  const showAutosavingToast = () => {
    BottomRightToaster.show(
      {
        intent: Intent.SUCCESS,
        message: <span>All changes saved</span>,
        timeout: 5000,
        isCloseButtonShown: true,
      },
      'autosaving'
    );
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await createReactionSearchPreferences(preset);

    changeLocation('/dashboard/settings/synthesis-plan-parameters');
  };

  const onPresetChange = async (updatedPreset: PresetEditable | Preset) => {
    if (isEqual(preset, updatedPreset)) {
      return;
    }

    setPreset(updatedPreset);

    if ('id' in updatedPreset && canBeSaved(updatedPreset)) {
      await patchReactionSearchPreferences(updatedPreset);
      showAutosavingToast();
    }
  };

  const onPresetSearchParamsChange: SearchParameterFormProps['onChange'] = updatedParams => {
    const updatedPreset = {
      ...preset,
      searchParams: updatedParams,
    };
    onPresetChange(updatedPreset);
  };

  const onPresetMetaChange = (updatedPresetMeta: Partial<PresetEditable>) => {
    const updatedPreset = {
      ...preset,
      ...updatedPresetMeta,
    };
    onPresetChange(updatedPreset);
  };

  return (
    <div data-test="reaction-search-parameters-page">
      <h4 className={cls.title}>
        {isNew ? 'Create new synthesis plan parameter preset' : 'Change synthesis plan parameter preset'}
      </h4>
      <form
        onSubmit={event => {
          onSubmit(event);
        }}
      >
        <SearchParameterForm
          before={
            <PresetMetaForm
              className={cls.paramGroup}
              preset={preset}
              onChange={onPresetMetaChange}
              isNameDuplicate={isNameDuplicate(preset.name)}
            />
          }
          className={cls.paramForm}
          groupClassName={cls.paramGroup}
          footerClassName={cls.footer}
          footer={
            isNew ? (
              <Button
                large
                intent={Intent.PRIMARY}
                disabled={isSubmitDisabled}
                loading={isPatching}
                text="Create parameter preset"
                type="submit"
                data-test="submit-button"
              />
            ) : null
          }
          searchParams={preset.searchParams}
          onChange={onPresetSearchParamsChange}
        />
      </form>
    </div>
  );
}

export default withRouter(
  connect(
    (state: any, ownProps: any) => ({
      ...ownProps,
      isPatching: state.reactionSearchPreference.patch.processing > 0,
      error: state.reactionSearchPreference.patch.error,
    }),
    {
      changeLocation,
      patchReactionSearchPreferences,
      createReactionSearchPreferences,
    }
  )(PresetForm)
);
